import React, { useState, useEffect, useContext } from "react";
import Table from "react-bootstrap/Table";
import PropTypes from "prop-types";
import { numberFormat, formatBopCode } from "../../common/helper";
import "./generic-table.css";
import { tradeSearch } from "../../api/trades.api";
import Loader from "../loader/loader";
import TradeContainer from "../trade-table/trade/trade-container";
import {format, parseISO} from 'date-fns'

import TradeContext from "../../store/tradeState/tradeContext";

const GenericTable = (props) => {
  // State management with useState
  const [page, setPage] = useState(1);
  const [recordCount, setRecordCount] = useState(1);
  const [pageCount, setPageCount] = useState(10);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [data, setData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  const [trade, setTrade] = useState({});
  const [showTrade, setShowTrade] = useState(false);

  const tradeCtx = useContext(TradeContext);

  // Fetch trades with optional silent mode (no loader)
  const pageTrade = async (silent = false) => {
    if (!silent) {
      setIsLoading(true);
    }
    try {
      const currentTrades = await tradeSearch("", "", page, rowsPerPage, [
        "-trade_date",
      ]);

      if (currentTrades) {
        // Only update if data has changed
        const newResults = currentTrades?.data?.results || [];
        const currentResults = data?.data?.results || [];
        
        // Compare by stringifying - checks if content is different
        const hasChanged = JSON.stringify(newResults) !== JSON.stringify(currentResults);
        
        if (hasChanged || !silent) {
          setData(currentTrades);
          setRecordCount(currentTrades?.data?.count || 0);
          setPageCount(currentTrades?.data?.total_pages || 0);
          
          // Update the selected trade if it exists in the new results
          if (trade?.id) {
            const updatedSelectedTrade = newResults.find(t => t.id === trade.id);
            if (updatedSelectedTrade) {
              setTrade(updatedSelectedTrade);
            }
          }
        }
      }
    } catch (error) {
      console.error("Error fetching trades:", error);
    } finally {
      if (!silent) {
        setIsLoading(false);
      }
    }
  };

  // Equivalent to componentDidMount using useEffect
  useEffect(() => {
    pageTrade();
  }, [page, rowsPerPage]); // Re-fetch when page or rowsPerPage changes

  // Polling effect - refetch trades every 15 seconds silently
  useEffect(() => {
    const intervalId = setInterval(() => {
      pageTrade(true); // Silent mode - no loader
    }, 15000); // 15 seconds

    // Cleanup interval on unmount
    return () => clearInterval(intervalId);
  }, [page, rowsPerPage, data]); // Re-create interval when pagination changes

  // Handle rows per page change
  const handleChange = (evt) => {
    const newRowsPerPage = parseInt(evt.target.value, 10);
    setRowsPerPage(newRowsPerPage);
    setPage(1); // Reset to first page when rows per page changes
  };

  // Page navigation handlers
  const pageUp = () => {
    if (page !== pageCount) {
      setPage((prevPage) => prevPage + 1);
    }
  };

  const pageDown = () => {
    if (page !== 1) {
      setPage((prevPage) => prevPage - 1);
    }
  };

  // ----------------------------------------
  const handleShowTrade = (trade) => {
    setTrade(trade);
    setShowTrade(true);
  };

  const handleTradeStatus = (trade) => {
    switch (trade.status) {
      case 'CREATED':
        return (
        <span
          className={`createdTrade statusColumn `}
        >
          Created
        </span>
      )
      case 'BOOKED':
        return (
        <span
          className={`createdTrade statusColumn `}
        >
          Booked
        </span>
      )
      case 'COMPLETED':
        return (
        <span
          className={`completedTrade statusColumn `}
        >
          Completed
        </span>
      )
      case 'CANCELLED':
        return (
        <span
          className={`cancelledTrade statusColumn `}
        >
          Cancelled
        </span>
      )
      case 'AWAITING_DOCUMENTS':
        return (
        <span
          className={`awaitingDocuments statusColumn `}
        >
          Awaiting Documents
        </span>
      )
      case 'AWAITING_AUTHORISATION':
        return (
        <span
          className={`awaitingAuthorisation statusColumn `}
        >
          Awaiting Authorisation
        </span>
      )
      case 'SUBMITTED_TO_BANK':
        return (
        <span
          className={`submittedToBank statusColumn `}
        >
          Submitted to Bank
        </span>
      )
      default:
        return (
        <span
          className={`createdTrade statusColumn `}
        >
          Unknown
        </span>
      )
    }
  };

  // Render logic
  return (
    <div className="paginated-table">
      <Table hover className="modern-table">
        <thead>
          <tr>
            <th>Customer Name</th>
            <th>Value Date</th>
            <th>Bank</th>
            <th style={{ width: "150px" }}>Trade Amount</th>
            <th style={{ width: "150px" }}>Exc. Rate</th>
            <th style={{ width: "150px" }}>Max Exc. Rate</th>
            <th style={{ width: "150px" }}>FX amount</th>
            <th>BOP code</th>
            <th>Status</th>
          </tr>
        </thead>
        <tbody>
          {isLoading ? (
            <tr>
              <td colSpan={9}>
                <Loader />
              </td>
            </tr>
          ) : data.data?.results?.length > 0 ? (
            data.data.results.map((d) => (
              <tr key={d.id} onClick={() => handleShowTrade(d)} className="clickable-row">
                <td>{d.customer?.name || ""}</td>
                <td>{format(parseISO(d.trade_date), 'MM/dd/yyyy HH:mm')}</td>
                <td>{d.bank?.display_name || ""}</td>
                <td className="right-text">{`R ${numberFormat(d.trade_amount)}`}</td>
                <td className="right-text">{d.trade_rate}</td>
                <td className="right-text">{d.max_exchange_rate}</td>
                <td className="right-text">{`${d.fx_currency?.symbol || ""} ${numberFormat(d.fx_amount)}`}</td>
                <td>{formatBopCode(d.bop_code)}</td>
                <td>{handleTradeStatus(d)}</td>
              </tr>
            ))
          ) : (
            <tr>
              <td colSpan={9}>There are currently no trades booked</td>
            </tr>
          )}
        </tbody>
      </Table>

      <div className="row mt-2 table-footer">
        <div className="col-8 flex-row"></div>
        <div className="col-4 flex-row">
          <label className="table-footer-label">Rows per page:</label>
          <select
            className="rpp-select"
            id="rows_per_page"
            name="rows_per_page"
            onChange={handleChange}
            value={rowsPerPage}
          >
            <option value={10}>10</option>
            <option value={15}>15</option>
            <option value={20}>20</option>
            <option value={30}>30</option>
            <option value={40}>40</option>
            <option value={50}>50</option>
            <option value={100}>100</option>
          </select>
          <label className="table-footer-label">
            {page} of {pageCount}
          </label>
          <button
            onClick={pageDown}
            className="btn btn-secondary pagination-nav"
            disabled={page === 1}
          >
            {`<`}
          </button>
          <button
            onClick={pageUp}
            className="btn btn-secondary pagination-nav"
            disabled={page === pageCount}
          >
            {`>`}
          </button>
        </div>
      </div>
      {showTrade && (
              <TradeContainer
                currentTrade={trade}
                showForm={showTrade}
                callbackShowForm={() => setShowTrade(true)}
                closeForm={() => {
                  setShowTrade(false);
                  pageTrade();
                }}
                callbackExecute={() => console.log("Execute Trade")}
                callbackCancel={() => console.log("Cancel Trade")}
                callbackAdd={() => console.log("Add Trade")}
                callbackRetry={() => {
                  setShowTrade(false);
                  tradeCtx.retryTrades([tradeCtx.trade.id]);
                  setTimeout(() => pageTrade(), 500);
                }}
                config={false}
              />
            )}
    </div>
  );
};

// PropTypes
GenericTable.propTypes = {
  data: PropTypes.array,
};

export default GenericTable;